import React, { ReactNode } from 'react';
import { List, Input, Button, Skeleton, Flex, Pagination } from 'antd';
import { useTranslation } from 'react-i18next';
import EmptyState from '../EmptyState/EmptyState.component';

export interface SelectableListItem {
  id: string;
}

export interface SelectableListProps<T extends SelectableListItem> {
  items: T[];
  loading?: boolean;
  multiSelect?: boolean;
  searchTerm: string;
  selectedItems: Record<string, boolean>;
  pagination?: {
    current: number;
    total: number;
    pageSize: number;
  };
  
  searchPlaceholder?: string;
  selectAllText?: string;
  emptyStateTitle?: string;
  emptyStateDescription?: string;
  emptyStateIcon?: string;
  createButtonText?: string;
  
  onSearchChange: (term: string) => void;
  onItemSelect: (id: string, selected: boolean) => void;
  onSelectAll?: () => void;
  onPageChange?: (page: number, pageSize: number) => void;
  onCreateClick?: () => void;
  
  renderItem: (item: T, isSelected: boolean, onSelect: (selected: boolean) => void) => ReactNode;
  getItemId?: (item: T) => string;
}

const SelectableList = <T extends SelectableListItem>({
  items,
  loading = false,
  multiSelect = true,
  searchTerm,
  selectedItems,
  pagination,
  searchPlaceholder,
  selectAllText,
  emptyStateTitle,
  emptyStateDescription,
  emptyStateIcon,
  createButtonText,
  onSearchChange,
  onItemSelect,
  onSelectAll,
  onPageChange,
  onCreateClick,
  renderItem,
  getItemId = (item) => item.id
}: SelectableListProps<T>) => {
  const { t } = useTranslation();

  const handleItemSelect = (item: T) => (selected: boolean) => {
    onItemSelect(getItemId(item), selected);
  };

  const showCreateButton = items.length === 0 && !loading && onCreateClick && createButtonText;
  const showEmptyState = items.length === 0 && !loading && !showCreateButton;

  return (
    <Flex vertical gap="middle">
      <Input
        placeholder={searchPlaceholder || t('search', { ns: 'standard' })}
        value={searchTerm}
        onChange={(e) => onSearchChange(e.target.value)}
      />

      {multiSelect && onSelectAll && items.length > 0 && (
        <Button onClick={onSelectAll}>
          {selectAllText || t('select-all', { ns: 'buttons' })}
        </Button>
      )}

      <div style={{ border: '1px solid #d9d9d9', padding: '16px', borderRadius: '8px' }}>
        {loading ? (
          <Skeleton active />
        ) : showEmptyState ? (
          <EmptyState
            icon={emptyStateIcon}
            title={emptyStateTitle || t('no-items', { ns: 'labels' })}
            description={emptyStateDescription || t('no-items-description', { ns: 'standard' })}
          />
        ) : showCreateButton ? (
          <Flex justify="center" align="center" style={{ minHeight: '120px' }}>
            <Button type="primary" onClick={onCreateClick}>
              {createButtonText}
            </Button>
          </Flex>
        ) : (
          <>
            <List
              itemLayout="horizontal"
              dataSource={items}
              renderItem={(item) => (
                <List.Item key={getItemId(item)}>
                  {renderItem(
                    item,
                    Boolean(selectedItems[getItemId(item)]),
                    handleItemSelect(item)
                  )}
                </List.Item>
              )}
            />

            {pagination && onPageChange && (
              <Flex justify="center" style={{ marginTop: '16px' }}>
                <Pagination
                  current={pagination.current}
                  total={pagination.total}
                  pageSize={pagination.pageSize}
                  onChange={onPageChange}
                  simple
                />
              </Flex>
            )}
          </>
        )}
      </div>
    </Flex>
  );
};

export default SelectableList;
